/**
 * Rattachements d'un paiement PAYGo aux recettes 5000 — pointage partiel.
 *
 * Un paiement peut désormais être réparti sur plusieurs recettes : la liste income_links
 * porte, recette par recette, le montant rattaché. Les documents antérieurs n'ont qu'un
 * income_id, qui vaut alors rattachement du paiement entier.
 *
 * Miroir des règles de domainModel (paymentIncomeLinks / paymentMatched / paymentRemaining) :
 * l'écran doit proposer exactement ce que le service acceptera, sinon il laisse saisir un
 * montant que le serveur refusera.
 */

import { MATCHING_FULL, MATCHING_NONE, MATCHING_PARTIAL } from './factId5000';

/** Tolérance de comparaison des montants, en unités de devise (la même que domainModel). */
export const AMOUNT_EPSILON = 0.01;

const toAmount = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

/** Arrondi au centime : les montants saisis ou recalculés ne doivent pas traîner de 0,000001. */
export const roundAmount = (value) => Math.round(toAmount(value) * 100) / 100;

/**
 * Liens normalisés d'un paiement : income_links s'il est renseigné, sinon le rattachement
 * historique (income_id seul = paiement entier), sinon aucun. Toujours des copies.
 */
export function incomeLinks(payment) {
    if (!payment) return [];
    if (Array.isArray(payment.income_links) && payment.income_links.length > 0) {
        return payment.income_links
            .filter((link) => link && link.income_id)
            .map((link) => ({ ...link, amount: toAmount(link.amount) }));
    }
    if (payment.income_id) {
        return [{ income_id: payment.income_id, amount: toAmount(payment.money) }];
    }
    return [];
}

/** Montant déjà pointé, toutes recettes confondues. */
export function matched(payment) {
    return incomeLinks(payment).reduce((sum, link) => sum + link.amount, 0);
}

/** Reste à pointer, jamais négatif. */
export function remaining(payment) {
    if (!payment) return 0;
    return Math.max(0, roundAmount(toAmount(payment.money) - matched(payment)));
}

/** Entièrement pointé : il ne reste rien à rattacher, à la tolérance près. */
export function isFullyMatched(payment) {
    return remaining(payment) <= AMOUNT_EPSILON;
}

/** Partiellement pointé : au moins un lien, et un reste. */
export function isPartiallyMatched(payment) {
    return incomeLinks(payment).length > 0 && !isFullyMatched(payment);
}

/**
 * État de pointage d'un paiement, dans le vocabulaire des opérations de stock (factId5000),
 * pour que la colonne « Pointage » se lise comme celle de la liste des ventes. Pas d'état
 * « incohérent » ici : les montants par recette sont portés par le paiement lui-même.
 */
export function paymentMatchingState(payment) {
    if (incomeLinks(payment).length === 0) return MATCHING_NONE;
    return isFullyMatched(payment) ? MATCHING_FULL : MATCHING_PARTIAL;
}

/**
 * Paiement annulé. Le champ JSON rendu par domainModel est `status` (minuscule) ; `Status`
 * n'est lu qu'à défaut, et la casse de la valeur est ignorée.
 */
export function isCancelled(payment) {
    return !!payment && String(payment.status || payment.Status || "").toLowerCase() === "cancel";
}

/** Un paiement peut être (encore) pointé s'il n'est pas annulé et qu'il lui reste un montant. */
export function canLink(payment) {
    return !!payment && !isCancelled(payment) && !isFullyMatched(payment);
}

/**
 * Filtre « Pointé / Non pointé » de la liste des paiements (true / false ; toute autre valeur
 * ne filtre pas). « Pointé » = au moins une recette rattachée ; « Non pointé » = un reste à
 * pointer, annulés compris comme auparavant. Un paiement partiel relève donc des deux.
 */
export function matchesLinkFilter(payment, value) {
    if (value === true) return incomeLinks(payment).length > 0;
    if (value === false) return !isFullyMatched(payment);
    return true;
}

/**
 * Erreur de saisie du « montant à pointer » d'une ligne, ou "" si le montant est acceptable.
 * Bornes : strictement positif, au plus le reste (à la tolérance près).
 */
export function amountError(value, max) {
    const n = parseFloat(value);
    if (value === "" || value === null || value === undefined || !Number.isFinite(n)) {
        return "Montant requis";
    }
    // Le montant envoyé est arrondi au centime, et domainModel refuse tout montant qui ne
    // dépasse pas la tolérance : c'est donc l'arrondi qu'il faut valider, sans quoi 0,004 ou
    // 0,006 passeraient ici pour être refusés par le service.
    const rounded = roundAmount(n);
    if (rounded <= AMOUNT_EPSILON) {
        return "Le montant doit dépasser 0,01 (arrondi au centime)";
    }
    if (rounded > toAmount(max) + AMOUNT_EPSILON) {
        return `Au plus ${roundAmount(max)}`;
    }
    return "";
}

/** income_id historique maintenu : celui du premier lien restant, "" s'il n'y en a plus. */
const withLinks = (payment, links) => ({
    ...payment,
    income_links: links,
    income_id: links.length > 0 ? links[0].income_id : "",
});

/**
 * Ajoute un rattachement. Un second pointage sur la même recette S'AJOUTE à la part existante,
 * il ne la remplace jamais. Sans montant valide, c'est le reste qui est rattaché.
 * Rend un nouvel objet : le paiement d'origine n'est pas modifié.
 */
export function addIncomeLink(payment, incomeID, amount) {
    let value = parseFloat(amount);
    if (!Number.isFinite(value) || value <= 0) {
        value = remaining(payment);
    }
    const links = incomeLinks(payment);
    const existing = links.find((link) => link.income_id === incomeID);
    if (existing) {
        existing.amount = roundAmount(existing.amount + value);
    } else {
        links.push({ income_id: incomeID, amount: roundAmount(value) });
    }
    return withLinks(payment, links);
}

/**
 * Retire un rattachement : en entier sans montant, sinon seulement ce montant (le lien disparaît
 * s'il tombe sous la tolérance). Rend un nouvel objet.
 */
export function removeIncomeLink(payment, incomeID, amount) {
    const value = parseFloat(amount);
    const partial = Number.isFinite(value) && value > 0;
    const links = incomeLinks(payment)
        .map((link) => {
            if (link.income_id !== incomeID) return link;
            if (!partial) return null;
            const left = roundAmount(link.amount - value);
            return left > AMOUNT_EPSILON ? { ...link, amount: left } : null;
        })
        .filter(Boolean);
    return withLinks(payment, links);
}
